import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type CartItemType = 'product' | 'service';

@Injectable({
  providedIn: 'root'
})
export class CartService {

  private readonly CART_KEY = 'cart';
  private readonly CART_WAREHOUSE_KEY = 'cartWarehouseId';

  private cartSubject = new BehaviorSubject<any[]>(this.loadCart());
  cart$ = this.cartSubject.asObservable();

  private cartCountSubject = new BehaviorSubject<number>(this.loadCart().length);
  cartCount$ = this.cartCountSubject.asObservable();

  constructor() { }

  // =========================
  // Public Methods
  // =========================

  getCart(): any[] {
    this.syncCartState();
    return this.cartSubject.value;
  }

  addOrToggleItem(item: any, itemType: CartItemType = 'product'): void {
    const cart = [...this.loadCart()];
    const cartItem = this.withCartType(item, itemType);
    const index = cart.findIndex(i => i.id === cartItem.id && this.getCartItemType(i) === this.getCartItemType(cartItem));

    if (index !== -1) {
      cart.splice(index, 1);
    } else {
      cart.push(cartItem);
    }

    this.updateCart(cart);
  }

  removeItem(item: any): void {
    const itemType = this.getCartItemType(item);
    const cart = this.loadCart().filter(i => !(i.id === item.id && this.getCartItemType(i) === itemType));
    this.updateCart(cart);
  }

  clearCart(): void {
    this.updateCart([]);
  }

  // =========================
  // Private Helpers
  // =========================

  private updateCart(cart: any[]): void {
    if (cart.length) {
      const currentWarehouseId = this.getCurrentWarehouseId();
      if (currentWarehouseId) {
        localStorage.setItem(this.CART_WAREHOUSE_KEY, currentWarehouseId);
      }
      localStorage.setItem(this.CART_KEY, JSON.stringify(cart));
    } else {
      localStorage.removeItem(this.CART_KEY);
      localStorage.removeItem(this.CART_WAREHOUSE_KEY);
    }

    this.cartSubject.next(cart);
    this.cartCountSubject.next(cart.length);
  }

  private loadCart(): any[] {
    const storedWarehouseId = localStorage.getItem(this.CART_WAREHOUSE_KEY);
    const currentWarehouseId = this.getCurrentWarehouseId();

    if (storedWarehouseId && currentWarehouseId && storedWarehouseId !== currentWarehouseId) {
      localStorage.removeItem(this.CART_KEY);
      localStorage.removeItem(this.CART_WAREHOUSE_KEY);
      return [];
    }

    const stored = localStorage.getItem(this.CART_KEY);

    try {
      return stored ? JSON.parse(stored) : [];
    } catch {
      localStorage.removeItem(this.CART_KEY);
      localStorage.removeItem(this.CART_WAREHOUSE_KEY);
      return [];
    }
  }

  private syncCartState(): void {
    const cart = this.loadCart();
    this.cartSubject.next(cart);
    this.cartCountSubject.next(cart.length);
  }

  private getCurrentWarehouseId(): string | null {
    return localStorage.getItem('posId');
  }

  private withCartType(item: any, itemType: CartItemType): any {
    return {
      ...item,
      cartItemType: this.getCartItemType(item, itemType)
    };
  }

  private getCartItemType(item: any, fallback: CartItemType = 'product'): CartItemType {
    return item?.cartItemType === 'service' ? 'service' : fallback;
  }
}
